/**
 * Copyright (c) 2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { AtomNumber } from './measures';
export var ElementNames = {
  H: 'Hydrogen',
  HE: 'Helium',
  LI: 'Lithium',
  BE: 'Beryllium',
  B: 'Boron',
  C: 'Carbon',
  N: 'Nitrogen',
  O: 'Oxygen',
  F: 'Fluorine',
  NE: 'Neon',
  NA: 'Sodium',
  MG: 'Magnesium',
  AL: 'Aluminum',
  SI: 'Silicon',
  P: 'Phosphorus',
  S: 'Sulfur',
  CL: 'Chlorine',
  AR: 'Argon',
  K: 'Potassium',
  CA: 'Calcium',
  SC: 'Scandium',
  TI: 'Titanium',
  V: 'Vanadium',
  CR: 'Chromium',
  MN: 'Manganese',
  FE: 'Iron',
  CO: 'Cobalt',
  NI: 'Nickel',
  CU: 'Copper',
  ZN: 'Zinc',
  GA: 'Gallium',
  GE: 'Germanium',
  AS: 'Arsenic',
  SE: 'Selenium',
  BR: 'Bromine',
  KR: 'Krypton',
  RB: 'Rubidium',
  SR: 'Strontium',
  Y: 'Yttrium',
  ZR: 'Zirconium',
  NB: 'Niobium',
  MO: 'Molybdenum',
  TC: 'Technetium',
  RU: 'Ruthenium',
  RH: 'Rhodium',
  PD: 'Palladium',
  AG: 'Silver',
  CD: 'Cadmium',
  IN: 'Indium',
  SN: 'Tin',
  SB: 'Antimony',
  TE: 'Tellurium',
  I: 'Iodine',
  XE: 'Xenon',
  CS: 'Cesium',
  BA: 'Barium',
  LA: 'Lanthanum',
  CE: 'Cerium',
  PR: 'Praseodymium',
  ND: 'Neodymium',
  PM: 'Promethium',
  SM: 'Samarium',
  EU: 'Europium',
  GD: 'Gadolinium',
  TB: 'Terbium',
  DY: 'Dysprosium',
  HO: 'Holmium',
  ER: 'Erbium',
  TM: 'Thulium',
  YB: 'Ytterbium',
  LU: 'Lutetium',
  HF: 'Hafnium',
  TA: 'Tantalum',
  W: 'Wolfram',
  RE: 'Rhenium',
  OS: 'Osmium',
  IR: 'Iridium',
  PT: 'Platinum',
  AU: 'Gold',
  HG: 'Mercury',
  TL: 'Thallium',
  PB: 'Lead',
  BI: 'Bismuth',
  PO: 'Polonium',
  AT: 'Astatine',
  RN: 'Radon',
  FR: 'Francium',
  RA: 'Radium',
  AC: 'Actinium',
  TH: 'Thorium',
  PA: 'Protactinium',
  U: 'Uranium',
  NP: 'Neptunium',
  PU: 'Plutonium',
  AM: 'Americium',
  CM: 'Curium',
  BK: 'Berkelium',
  CF: 'Californium',
  ES: 'Einsteinium',
  FM: 'Fermium',
  MD: 'Mendelevium',
  NO: 'Nobelium',
  LR: 'Lawrencium',
  RF: 'Rutherfordium',
  DB: 'Dubnium',
  SG: 'Seaborgium',
  BH: 'Bohrium',
  HS: 'Hassium',
  MT: 'Meitnerium',
  DS: 'Darmstadtium',
  RG: 'Roentgenium',
  CN: 'Copernicium',
  NH: 'Nihonium',
  FL: 'Flerovium',
  MC: 'Moscovium',
  LV: 'Livermorium',
  TS: 'Tennessine',
  OG: 'Oganesson'
};
export var AlkaliMetals = new Set(['LI', 'NA', 'K', 'RB', 'CS', 'FR']);
export function isAlkaliMetal(element) {
  return AlkaliMetals.has(element);
}
export var AlkalineEarthMetals = new Set(['BE', 'MG', 'CA', 'SR', 'BA', 'RA']);
export function isAlkalineEarthMetal(element) {
  return AlkalineEarthMetals.has(element);
}
export var PolyatomicNonmetals = new Set(['C', 'P', 'S', 'SE']);
export function isPolyatomicNonmetal(element) {
  return PolyatomicNonmetals.has(element);
}
export var DiatomicNonmetals = new Set(['H', 'N', 'O', 'F', 'CL', 'BR', 'I']);
export function isDiatomicNonmetal(element) {
  return DiatomicNonmetals.has(element);
}
export var NobleGases = new Set(['HE', 'NE', 'AR', 'KR', 'XE', 'RN']);
export function isNobleGas(element) {
  return NobleGases.has(element);
}
export var PostTransitionMetals = new Set(['ZN', 'GA', 'CD', 'IN', 'SN', 'HG', 'TI', 'PB', 'BI', 'PO', 'CN']);
export function isPostTransitionMetal(element) {
  return PostTransitionMetals.has(element);
}
export var Metalloids = new Set(['B', 'SI', 'GE', 'AS', 'SB', 'TE', 'AT']);
export function isMetalloid(element) {
  return Metalloids.has(element);
}
export var Halogens = new Set(['F', 'CL', 'BR', 'I', 'AT']);
export function isHalogen(element) {
  return Halogens.has(element);
}
export function isTransitionMetal(element) {
  var no = AtomNumber(element);
  return no >= 21 && no <= 29 || no >= 39 && no <= 47 || no >= 72 && no <= 79 || no >= 104 && no <= 108;
}
export function isLanthanide(element) {
  var no = AtomNumber(element);
  return no >= 57 && no <= 71;
}
export function isActinide(element) {
  var no = AtomNumber(element);
  return no >= 89 && no <= 103;
}
export function isMetal(element) {
  return isAlkaliMetal(element) || isAlkalineEarthMetal(element) || isLanthanide(element) || isActinide(element) || isTransitionMetal(element) || isPostTransitionMetal(element);
}
export function isNonmetal(element) {
  return isDiatomicNonmetal(element) || isPolyatomicNonmetal(element) || isNobleGas(element);
}